import React, { useState } from 'react';
import { Question, isQuestionContent } from "@/types/test";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Clock, 
  Award, 
  Target, 
  Pencil, 
  Trash2, 
  RefreshCw, 
  FileText,
  ListChecks,
  Mic,
  Video,
  Type,
  Code,
  AlertTriangle,
  Check
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { validateQuestion } from "./utils/questionValidation";
import { RegenerationFeedbackDialog } from "./RegenerationFeedbackDialog";

interface QuestionCardViewProps {
  question: Question;
  questionNumber: number;
  onEdit: () => void;
  onDelete: (id: string) => void;
  onRegenerate?: (id: string, feedback: string) => Promise<void>;
  onMarkscheme: () => void;
}

export const QuestionCardView = ({
  question,
  questionNumber,
  onEdit,
  onDelete,
  onRegenerate,
  onMarkscheme,
}: QuestionCardViewProps) => {
  const [showRegenerationDialog, setShowRegenerationDialog] = useState(false);
  const getQuestionText = () => {
    if (isQuestionContent(question.content)) {
      return question.content.question;
    }
    return typeof question.content === 'string' ? question.content : 'No question text';
  };

  const getQuestionOptions = () => {
    if (isQuestionContent(question.content) && question.content.options) {
      return question.content.options;
    }
    return [];
  };

  const getCorrectOption = () => {
    if (isQuestionContent(question.content) && question.content.correctOption !== undefined) {
      return question.content.correctOption;
    }
    return null;
  };

  const getQuestionTypeIcon = () => {
    switch (question.question_type) {
      case 'multiple_choice':
        return <ListChecks className="h-3 w-3" />;
      case 'essay':
        return <Type className="h-3 w-3" />;
      case 'audio_response':
        return <Mic className="h-3 w-3" />;
      case 'video_response':
        return <Video className="h-3 w-3" />;
      case 'coding':
        return <Code className="h-3 w-3" />;
      default:
        return <Type className="h-3 w-3" />;
    }
  };

  const getQuestionTypeLabel = () => {
    switch (question.question_type) {
      case 'multiple_choice':
        return 'Multiple Choice';
      case 'essay':
        return 'Essay';
      case 'audio_response':
        return 'Audio Response';
      case 'video_response':
        return 'Video Response';
      case 'coding':
        return 'Coding';
      default:
        return 'Unknown';
    }
  };

  const getDifficultyColor = () => {
    switch (question.difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'medium':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200';
      case 'hard':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatTime = (seconds: number | null) => {
    if (!seconds) return 'No limit';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  };

  const isValid = validateQuestion(question);
  const hasMarkscheme = question.markscheme && question.markscheme.criteria && question.markscheme.criteria.length > 0;

  const handleRegenerateWithFeedback = async (feedback: string) => {
    if (!onRegenerate) return;
    await onRegenerate(question.id, feedback);
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex items-start gap-3 flex-1">
          <span className="font-bold text-lg text-muted-foreground min-w-[2.5rem]">Q{questionNumber}</span>
        </div>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onEdit}
                  className="h-8 px-2"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit question</p>
              </TooltipContent>
            </Tooltip>
            
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onMarkscheme}
                  className="h-8 px-2"
                >
                  <FileText className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{hasMarkscheme ? "View markscheme" : "Create markscheme"}</p>
              </TooltipContent>
            </Tooltip>

            {onRegenerate && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowRegenerationDialog(true)}
                    className="h-8 px-2"
                  >
                    <RefreshCw className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Regenerate question</p>
                </TooltipContent>
                </Tooltip>
            )}

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(question.id)}
                  className="h-8 px-2 text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Delete question</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Question Text - Prominent */}
        <div className="prose prose-sm max-w-none">
          <p className="text-base font-medium text-foreground leading-relaxed">
            {getQuestionText()}
          </p>
        </div>

        {/* MCQ Options - Only for multiple choice questions */}
        {question.question_type === 'multiple_choice' && getQuestionOptions().length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Options:</p>
            <div className="grid gap-2">
              {getQuestionOptions().map((option, index) => (
                <div key={index} className="flex items-start gap-2 text-sm text-foreground">
                  <span className="font-medium text-muted-foreground min-w-[1.5rem]">
                    {String.fromCharCode(65 + index)}.
                  </span>
                  {getCorrectOption() === index && (
                    <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  )}
                  <span className="flex-1">{option}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Markscheme Overall Guidance */}
        {question.markscheme?.overall_guidance && (
          <>
            <div className="border-t border-border pt-3">
              <p className="text-sm font-medium text-muted-foreground mb-2">Markscheme Guidance:</p>
              <div className="text-sm text-foreground bg-muted/30 p-3 rounded-md border-l-2 border-primary/30">
                {question.markscheme.overall_guidance}
              </div>
            </div>
          </>
        )}

        {/* Metadata Strip - Subtle */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {/* Question Type */}
          <div className="flex items-center gap-1">
            {getQuestionTypeIcon()}
            <span>{getQuestionTypeLabel()}</span>
          </div>
          
          {/* Time Limit */}
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{formatTime(question.time_limit)}</span>
          </div>
          
          {/* Marks */}
          <div className="flex items-center gap-1">
            <Award className="w-3 h-3" />
            <span>{question.marks || 0} {question.marks === 1 ? 'mark' : 'marks'}</span>
          </div>
          
          {/* Difficulty with colored dot */}
          {question.difficulty && (
            <div className="flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full ${
                question.difficulty === 'easy' ? 'bg-green-500' :
                question.difficulty === 'medium' ? 'bg-yellow-500' :
                'bg-red-500'
              }`} />
              <span>{question.difficulty.charAt(0).toUpperCase() + question.difficulty.slice(1)}</span>
            </div>
          )}
          
          {/* Skill */}
          {question.skill && (
            <div className="flex items-center gap-1">
              <Target className="w-3 h-3" />
              <span>{question.skill}</span>
            </div>
          )}
          
          {/* Markscheme Status */}
          <div className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            <span className={hasMarkscheme ? "text-green-600" : "text-orange-600"}>
              {hasMarkscheme ? "Has markscheme" : "No markscheme"}
            </span>
          </div>
          
          {/* Validation Warning */}
          {!isValid && (
            <div className="flex items-center gap-1 text-orange-600">
              <AlertTriangle className="w-3 h-3" />
              <span>Incomplete</span>
            </div>
          )}
        </div>
      </CardContent>
      
      {/* Regeneration Dialog */}
      {onRegenerate && (
        <RegenerationFeedbackDialog
          open={showRegenerationDialog}
          onOpenChange={setShowRegenerationDialog}
          question={question}
          questionNumber={questionNumber}
          onRegenerate={handleRegenerateWithFeedback}
        />
      )}
    </Card>
  );
};